import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { privateAPI as api } from "@/api/axios";
import { Button, Heading } from "@/ui";
import { formatDateToDDMMYYYY } from "@/utils";

interface Note {
  id: number;
  content: string;
  created_at: string;
  created_by: string;
}

interface NotesTabProps {
  /** API endpoint for listing and creating notes, e.g. "/users/123/notes" */
  endpoint: string;
  queryKey: string[];
}

export const NotesTab = ({ endpoint, queryKey }: NotesTabProps) => {
  const [newNote, setNewNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: notes, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await api.get<{ data: Note[] }>(endpoint);
      return response.data.data;
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    setIsSubmitting(true);
    try {
      await api.post(endpoint, { content: newNote });
      setNewNote("");
      void refetch();
    } catch (error) {
      console.error("Failed to add note", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 pt-4">
      <div className="flex items-center justify-between">
        <Heading level={3}>Notes</Heading>
      </div>

      <form
        onSubmit={handleSubmit}
        className="border-gray-200 rounded-lg border bg-white p-4 shadow-sm"
      >
        <label
          htmlFor="note-content"
          className="text-gray-700 mb-2 block text-sm font-medium"
        >
          Add a Note
        </label>
        <textarea
          id="note-content"
          rows={3}
          className="border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border p-2 shadow-sm sm:text-sm"
          placeholder="Enter note details..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
        />
        <div className="mt-3 flex justify-end">
          <Button type="submit" disabled={isSubmitting || !newNote.trim()}>
            {isSubmitting ? "Saving..." : "Save Note"}
          </Button>
        </div>
      </form>

      <div className="flex flex-col gap-4">
        {notes?.map((note) => (
          <div
            key={note.id}
            className="border-gray-200 rounded-lg border bg-white p-4 shadow-sm"
          >
            <div className="mb-2 flex items-start justify-between">
              <span className="text-gray-900 text-sm font-bold">
                {note.created_by}
              </span>
              <span className="text-gray-500 text-xs">
                {formatDateToDDMMYYYY(new Date(note.created_at))}
              </span>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap text-sm">
              {note.content}
            </p>
          </div>
        ))}

        {notes?.length === 0 && (
          <p className="text-gray-500 py-8 text-center italic">No notes yet.</p>
        )}
      </div>
    </div>
  );
};
